import type {
  BoardFilterCondition,
  BoardFilterMode,
  BoardFilterSetData,
  BoardFilterViewSettings,
} from '../../board-v3.types';
import { DraftCondition, FilterFieldOption, filterFieldKey, operatorsForField } from './filters.config';

export function serializeFilterCondition(
  condition: DraftCondition,
  fieldsByKey: ReadonlyMap<string, FilterFieldOption>,
): BoardFilterCondition | null {
  const field = fieldsByKey.get(condition.key);
  if (!field) return null;

  const operator = operatorsForField(field).find((operator) => operator.value === condition.operator);
  if (!operator) return null;

  let value: unknown = condition.value;
  if (operator.isRange) value = [condition.value, condition.valueTo];

  const conditionHasValue = operator.requiresValue === false || hasValue(value);
  if (!conditionHasValue) return null;

  if (field.filter_type === 'number' && operator.value !== 'between') value = toNumberValue(value);
  if (field.filter_type === 'number' && operator.value === 'between' && Array.isArray(value)) {
    value = value.map((item) => toNumberValue(item));
  }

  return {
    key: filterFieldKey(field),
    field_key: filterFieldKey(field),
    type: operator.value,
    operator: operator.value,
    value,
    is_custom: !field.is_system,
    field_type: field.input_type === 'price' ? 'price' : field.filter_type,
  };
}

function hasValue(value: unknown): boolean {
  if (Array.isArray(value)) return value.length > 0 && value.every((item) => hasValue(item));
  if (typeof value === 'string') return value.trim().length > 0;
  return value !== null && value !== undefined && value !== '';
}

function toNumberValue(value: unknown): unknown {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : value;
}

export function deserializeFilterCondition(condition: BoardFilterCondition, localId: string): DraftCondition {
  const { key, type, value } = condition;
  const isRange = type === 'between' && Array.isArray(value);

  return {
    localId,
    key,
    operator: type,
    value: isRange ? value[0] : value,
    valueTo: isRange ? value[1] : null,
  };
}

export function serializeFilterDraft(
  mode: BoardFilterMode,
  view: BoardFilterViewSettings,
  conditions: DraftCondition[],
  fieldsByKey: ReadonlyMap<string, FilterFieldOption>,
): BoardFilterSetData {
  return {
    version: 1,
    mode,
    conditions: conditions
      .map((condition) => serializeFilterCondition(condition, fieldsByKey))
      .filter((condition): condition is BoardFilterCondition => !!condition),
    view: { ...view },
  };
}
